import React, { useState, useEffect } from 'react';
import { get, post, put } from '../../../../services/apiController';
import { toast } from 'react-toastify';
import { TABLE_DEFAULT_LIMIT, monthsDropdown } from '../../../../util/constant';
import { Table, Modal, Button, Form, Col, Row, FloatingLabel } from 'react-bootstrap';
import PaginationInPage from '../../../Pagination';
import { dateFormat } from '../../../../util/helper';
import { EditIcon } from '../../../icons';
import Select from 'react-select';
import { saveAs } from 'file-saver';
import { GrDocumentPdf } from 'react-icons/gr';

const years = Array.from({ length: 4 }, (_, i) => {
  const year = 2024 + i;
  return { value: year, label: year.toString() };
});

const DieselLogEntry = ({ installationId }) => {
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [logs, setLogs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    date: '',
    startingReading: '',
    endingReading: '',
    startingKwh: '',
    endingKwh: '',
  });

  const fetchLogEntries = (downloadPdf = false) => {
    const { month, year } = filters;
    let url = `/mm/diesel-log-entries/?installationId=${installationId}&month=${month}&year=${year}`;
    if (downloadPdf) {
      url += `&downloadPdf=true`;
    } else {
      url += `&page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}`;
    }
    get(url, { responseType: downloadPdf ? 'arraybuffer' : 'json' })
      .then((response) => {
        if (downloadPdf) {
          const fileName = `Machine Diesel Log Report - ${monthsDropdown.find((m) => m.value === month)?.label} ${year}.pdf`;
          const blob = new Blob([response], { type: 'application/pdf' });
          saveAs(blob, fileName);
        } else {
          setLogs(response?.data?.rows);
          setTotalRecords(response?.data?.count);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.response?.data?.message || err.message);
      });
  };

  useEffect(() => {
    fetchLogEntries();
  }, [currentPage, filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async (e) => {
    try {
      e.preventDefault();
      setIsSubmitting(true);
      const formattedData = {
        ...formData,
        startingReading: parseFloat(formData.startingReading).toFixed(2),
        endingReading: parseFloat(formData.endingReading).toFixed(2),
        startingKwh: parseFloat(formData.startingKwh).toFixed(2),
        endingKwh: parseFloat(formData.endingKwh).toFixed(2),
      };
      if (formData.id) {
        await put(formattedData, `/mm/diesel-log-entries/${formData.id}`);
        toast.success('Log entry updated successfully!');
      } else {
        await post({ ...formattedData, installationId }, `/mm/diesel-log-entries`);
        toast.success('Log entry created successfully!');
      }
      setShowModal(false);
      fetchLogEntries();
    } catch (err) {
      console.log('err', err?.response, err);
      toast.error(err?.response?.data?.data?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (log) => {
    setFormData(log);
    setShowModal(true);
  };

  const handleAddNew = async () => {
    try {
      const response = await get(`/mm/diesel-log-entries/last-log/${installationId}`);
      const lastLog = response?.data || {};
      setFormData({
        id: null,
        date: '',
        startingReading: lastLog.endingReading || '',
        endingReading: '',
        startingKwh: lastLog.endingKwh || '',
        endingKwh: '',
      });
    } catch (error) {
      setFormData({
        id: null,
        date: '',
        startingReading: '',
        endingReading: '',
        startingKwh: '',
        endingKwh: '',
      });
    }
    setShowModal(true);
  };

  return (
    <div>
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Logs | {totalRecords ?? 0}
          </h4>
          <div className="d-flex align-items-center gap-2">
            <Select
              value={monthsDropdown.find((m) => m.value === filters.month)}
              options={monthsDropdown}
              onChange={(selectedOption) => setFilters((prev) => ({ ...prev, month: selectedOption?.value }))}
              placeholder="Select Month"
              isClearable
              isSearchable
            />
            <Select
              value={years.find((y) => y.value === filters.year)}
              options={years}
              onChange={(selectedOption) => setFilters((prev) => ({ ...prev, year: selectedOption?.value }))}
              placeholder="Select Year"
              isClearable
              isSearchable
            />
            <GrDocumentPdf
              style={{ cursor: 'pointer', width: '2rem', height: '2rem' }}
              onClick={() => fetchLogEntries(true)}
              className="react-icons red"
              title="download pdf"
            />
            <button type="button" className="btn app-btn-primary" onClick={handleAddNew}>
              Create Log
            </button>
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>S.No.</th>
                    <th>Date</th>
                    <th>Starting Reading (Hrs)</th>
                    <th>Ending Reading (Hrs)</th>
                    <th>Starting KWH</th>
                    <th>Ending KWH</th>
                    <th>Total Hrs</th>
                    <th>Total KWH</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((item, index) => (
                    <tr key={item?.id}>
                      <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                      <td>{dateFormat(item?.date)}</td>
                      <td>{(parseFloat(item?.startingReading) || 0).toFixed(2)}</td>
                      <td>{(parseFloat(item?.endingReading) || 0).toFixed(2)}</td>
                      <td>{(parseFloat(item?.startingKwh) || 0).toFixed(2)}</td>
                      <td>{(parseFloat(item?.endingKwh) || 0).toFixed(2)}</td>
                      <td>{(parseFloat(item?.endingReading) - parseFloat(item?.startingReading) || 0).toFixed(2)}</td>
                      <td>{(parseFloat(item?.endingKwh) - parseFloat(item?.startingKwh) || 0).toFixed(2)}</td>
                      <td>
                        <span onClick={() => handleEdit(item)} className="edit-icon">
                          <EditIcon style={{ color: 'teal' }} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <Modal size="lg" show={showModal} backdrop="static" keyboard={false} className="request-modal">
          <Modal.Header>
            <Modal.Title>{formData.id ? 'Edit Log Entry' : 'Add Log Entry'}</Modal.Title>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowModal(false)}></button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSave}>
              <Row>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Date" className="mb-3">
                    <Form.Control
                      required
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      placeholder="Select Date"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Starting Reading (Hrs)" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="startingReading"
                      value={formData.startingReading}
                      onChange={handleInputChange}
                      placeholder="Enter Starting Reading (Hrs)"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Ending Reading (Hrs)" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="endingReading"
                      value={formData.endingReading}
                      onChange={handleInputChange}
                      placeholder="Enter Ending Reading (Hrs)"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Starting KWH" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="startingKwh"
                      value={formData.startingKwh}
                      onChange={handleInputChange}
                      placeholder="Enter Starting KWH"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Ending KWH" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="endingKwh"
                      value={formData.endingKwh}
                      onChange={handleInputChange}
                      placeholder="Enter Ending KWH"
                    />
                  </FloatingLabel>
                </Col>
              </Row>
              <div className="container text-center">
                <Button type="submit" className="add-request-btn mt-4 mb-3" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default DieselLogEntry;
